import { createContext, useContext } from 'react';
import { filterDataContext } from './FilterDataProvider';
import { useRemembered, useMemoryKey } from '../utilities/pageMemory';

// Step 8: one place holding everything that narrows the grant list —
// the search text and the deadline/amount ranges. The checkbox choices
// stay in FilterDataProvider; useActiveCriteria() combines them all.
export const grantFilterContext = createContext(null);

// Full state names (as the checkboxes show them) to the two-letter codes
// new records carry in "States".
export const STATE_CODES = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA',
  kansas: 'KS', kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
  'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH',
  oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI',
  'south carolina': 'SC', 'south dakota': 'SD', tennessee: 'TN', texas: 'TX',
  utah: 'UT', vermont: 'VT', virginia: 'VA', washington: 'WA',
  'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY', dc: 'DC',
  'puerto rico': 'PR', 'puerto rico (usa)': 'PR',
};

export function GrantFilterProvider({ children }) {
  // Step 31: the search words, the date box and the two amount boxes are
  // remembered per user, so the page reopens as it was left.
  const [searchText, setSearchText] = useRemembered(useMemoryKey('search'), '');
  const [deadlineAfter, setDeadlineAfter] = useRemembered(useMemoryKey('deadlineAfter'), '');
  const [amountMin, setAmountMin] = useRemembered(useMemoryKey('amountMin'), '');
  const [amountMax, setAmountMax] = useRemembered(useMemoryKey('amountMax'), '');

  return (
    <grantFilterContext.Provider
      value={{
        searchText, setSearchText,
        deadlineAfter, setDeadlineAfter,
        amountMin, setAmountMin,
        amountMax, setAmountMax,
      }}
    >
      {children}
    </grantFilterContext.Provider>
  );
}

// Everything currently narrowing the list, as one plain object.
export function useActiveCriteria() {
  const ranges = useContext(grantFilterContext);
  const { filterData, getActiveFilters } = useContext(filterDataContext);
  const pairs = getActiveFilters ? getActiveFilters(filterData) || [] : [];
  const byCategory = {};
  pairs.forEach(({ category, value }) => {
    (byCategory[category] = byCategory[category] || []).push(value);
  });
  return { ...ranges, byCategory };
}

// Step 33, owner's order: a foundation grant is one of three kinds, and the
// three are told apart. "Private Foundation" is only a record whose Agency is
// exactly that; "Public Foundation" likewise; "Foundation" is a record that
// says foundation without being one of those two (so "Foundation" itself and
// "Foundation / Corporation" match, the private and public kinds do not).
// Every other word is matched inside the Agency as before, so "Corporation"
// still finds "Foundation / Corporation". Both sides of the ticked value are
// lowercased and trimmed before they get here. The program
// (grant_filters.py) applies the same rule, word for word.
export function agencyPartMatches(agency, part) {
  // A stray space either side of the record's own words must not turn a
  // Private Foundation into an unspecified one, so it is taken off first.
  const value = agency.trim();
  if (part === 'private foundation') return value === 'private foundation';
  if (part === 'public foundation') return value === 'public foundation';
  if (part === 'foundation') {
    return value.includes('foundation')
      && value !== 'private foundation'
      && value !== 'public foundation';
  }
  return value.includes(part);
}

// Does one grant pass the criteria? Returns "yes", "no", or "no-info"
// (the grant lacks the field an active filter needs — counted honestly,
// never silently dropped).
export function grantPasses(grant, criteria) {
  const { searchText, deadlineAfter, amountMin, amountMax, byCategory } = criteria;

  if (searchText) {
    const haystack = [
      grant['Grant Name'], grant['Grant Description'],
      grant['Funding Source'], grant['Categories'],
    ].join(' ').toLowerCase();
    if (!haystack.includes(searchText.toLowerCase())) return 'no';
  }

  for (const category of Object.keys(byCategory)) {
    const wanted = byCategory[category];
    if (category === 'Categories') {
      const list = Array.isArray(grant['Categories List'])
        ? grant['Categories List'].join(' ') : '';
      const text = `${list} ${grant['Categories'] || ''}`.toLowerCase();
      if (!text.trim()) return 'no-info';
      if (!wanted.some((w) => text.includes(w.toLowerCase()))) return 'no';
    } else if (category === 'Agency Type') {
      const agency = (grant['Agency'] || '').toLowerCase();
      if (!agency) return 'no-info';
      const matches = wanted.some((w) =>
        w.toLowerCase().split(/[/,]/).some(
          (part) => part.trim() && agencyPartMatches(agency, part.trim())));
      if (!matches) return 'no';
    } else if (category === 'Business Type') {
      // Step 34: no grant says "For Profit" or "B Corp" in those words, so the
      // three page choices are read off the record's "Applicant Types".
      // The program (grant_filters.py) applies exactly the same three rules.
      const types = grant['Applicant Types'];
      const text = (Array.isArray(types) ? types.join(' ') : (types || ''))
        .toString().toLowerCase();
      if (!text.trim()) return 'no-info';
      const matches = wanted.some((w) => {
        if (w === 'Non-Profit') return /non[ -]?profit|charit/.test(text);
        if (w === 'For-Profit') return /for[ -]?profit|small business|b corp/.test(text);
        if (w === 'Individual') return /individual/.test(text);
        if (w === 'School / University') return /school|university|college/.test(text);
        if (w === 'Government / Public Agency') {
          return /government|public agency|municipal|municipality/.test(text);
        }
        return false;
      });
      if (!matches) return 'no';
    } else if (category === 'Geographic Focus') {
      const codes = Array.isArray(grant['States']) ? grant['States'] : [];
      const text = (grant['Geographic Focus'] || '').toLowerCase();
      if (!codes.length && !text) return 'no-info';
      const matches = wanted.some((w) => {
        const code = STATE_CODES[w.toLowerCase()];
        return (code && codes.includes(code)) || text.includes(w.toLowerCase());
      });
      if (!matches) return 'no';
    }
  }

  if (deadlineAfter) {
    const type = grant['Deadline Type'];
    const time = Date.parse(grant['Deadline Date']);
    if (!type && isNaN(time)) return 'no-info';
    // apply-anytime and repeating grants stay; fixed dates must not be past
    if (type !== 'Ongoing' && type !== 'Recurring') {
      if (isNaN(time)) return 'no-info';
      if (time < Date.parse(deadlineAfter)) return 'no';
    }
  }

  if (amountMin !== '' || amountMax !== '') {
    const gMin = grant['Amount Min'];
    const gMax = grant['Amount Max'];
    if (gMin == null && gMax == null) return 'no-info';
    const lo = gMin != null ? gMin : gMax;
    const hi = gMax != null ? gMax : gMin;
    if (amountMax !== '' && lo > Number(amountMax)) return 'no';
    if (amountMin !== '' && hi < Number(amountMin)) return 'no';
  }

  return 'yes';
}
